import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Panel } from "primereact/panel";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import React, { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import agendamentoService from "../../services/agendamento.service";
import apoioService from "../../services/apoio.service";
import authService from "../../services/auth.service";
import { formatarData } from "../../util/FormatarData";
import { situacaoAgendamento } from "../../util/SituacaoAgendamento";
import DialogPaciente from "../common/dialogPaciente.component";
import DialogProfissional from "../common/dialogProfissional.component";
import DialogSala from "../common/dialogSala.component";
import DialogAgendamentoInfo from "./dialogAgendamentoInfo.component";

const FILTROS_SEM_PERIODO = {
  paciente: null,
  profissional: null,
  sala: null,
  situacaoAgendamentoId: null,
  dataInicial: null,
  dataFinal: null,
  presencial: null,
};

const LAZY_INICIAL = {
  first: 0,
  rows: 10,
  page: 0,
  sortField: "dataAgendamento",
  sortOrder: false,
};

const MODALIDADES = [
  { label: "Presencial", value: true },
  { label: "Online", value: false },
];

const PERIODOS = [
  { label: "Dia", value: "dia" },
  { label: "Semana", value: "semana" },
  { label: "Mês", value: "mes" },
];

const PERIODO_PADRAO = "semana";

const ORDENACOES = [
  { label: "Data (mais recente)", value: "dataAgendamento:false" },
  { label: "Data (mais antiga)", value: "dataAgendamento:true" },
  { label: "Paciente (A-Z)", value: "pacienteNome:true" },
  { label: "Profissional (A-Z)", value: "profissionalNome:true" },
  { label: "Situação", value: "situacaoAgendamentoId:true" },
];

const semHoras = (data) => new Date(data.getFullYear(), data.getMonth(), data.getDate());

/** Intervalo fechado (inicio e fim inclusivos) que o modo cobre em torno da data de referência. */
const intervaloPeriodo = (modo, referencia) => {
  const base = semHoras(referencia);

  if (modo === "dia") return { inicio: base, fim: base };

  if (modo === "semana") {
    const inicio = new Date(base);
    inicio.setDate(base.getDate() - base.getDay()); // domingo, como no calendário
    const fim = new Date(inicio);
    fim.setDate(inicio.getDate() + 6);
    return { inicio, fim };
  }

  return {
    inicio: new Date(base.getFullYear(), base.getMonth(), 1),
    fim: new Date(base.getFullYear(), base.getMonth() + 1, 0),
  };
};

const deslocarReferencia = (modo, referencia, passos) => {
  const nova = semHoras(referencia);
  if (modo === "dia") nova.setDate(nova.getDate() + passos);
  else if (modo === "semana") nova.setDate(nova.getDate() + 7 * passos);
  else nova.setMonth(nova.getMonth() + passos);
  return nova;
};

/** Estado inicial: a semana corrente, a mesma visão com que o calendário abre. */
const filtrosIniciais = () => {
  const { inicio, fim } = intervaloPeriodo(PERIODO_PADRAO, new Date());
  return { ...FILTROS_SEM_PERIODO, dataInicial: inicio, dataFinal: fim };
};

const rotularPeriodo = (modo, referencia, temDatas) => {
  if (!modo) return temDatas ? "Período personalizado" : "Todos os períodos";

  if (modo === "dia")
    return referencia.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  if (modo === "mes") {
    const rotulo = referencia.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    return rotulo.charAt(0).toUpperCase() + rotulo.slice(1);
  }

  const { inicio, fim } = intervaloPeriodo(modo, referencia);
  const dia = (data) => data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  return `${dia(inicio)} a ${dia(fim)}/${fim.getFullYear()}`;
};

const converterData = (data) => {
  if (!data) return null;
  const dia = data.getDate().toString().padStart(2, "0");
  const mes = (data.getMonth() + 1).toString().padStart(2, "0");
  return `${dia}/${mes}/${data.getFullYear()}`;
};

const converterValor = (valor) => {
  if (valor === null || valor === undefined) return "";
  return (
    "R$ " +
    valor.toLocaleString("pt-br", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

const AgendamentoConsulta = ({ mostrarMensagem }) => {
  const permissaoPesquisaPaciente = authService.checkIfHasPermissao(["PES_PACIENTE"]);
  const permissaoPesquisaProfissional = authService.checkIfHasPermissao(["PES_PROFISSIONAL"]);
  const permissaoPesquisaSala = authService.checkIfHasPermissao(["PES_SALA"]);

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const [filtros, setFiltros] = useState(filtrosIniciais);
  const [lazyState, setLazyState] = useState(LAZY_INICIAL);
  const [periodo, setPeriodo] = useState({ modo: PERIODO_PADRAO, referencia: new Date() });
  const [filtrosRecolhidos] = useState(isMobile);

  const [agendamentos, setAgendamentos] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);

  const [situacoes, setSituacoes] = useState([]);

  const [dialogPacienteVisible, setDialogPacienteVisible] = useState(false);
  const [dialogProfissionalVisible, setDialogProfissionalVisible] = useState(false);
  const [dialogSalaVisible, setDialogSalaVisible] = useState(false);

  const [dialogInfoVisible, setDialogInfoVisible] = useState(false);
  const [agendamento, setAgendamento] = useState({});

  const pesquisarAgendamentos = useCallback(() => {
    setLoading(true);
    agendamentoService
      .pesquisarAgendamentos({
        ...lazyState,
        pacienteId: filtros.paciente?.id,
        profissionalId: filtros.profissional?.id,
        salaId: filtros.sala?.id,
        situacaoAgendamentoId: filtros.situacaoAgendamentoId,
        dataInicial: converterData(filtros.dataInicial),
        dataFinal: converterData(filtros.dataFinal),
        presencial: filtros.presencial,
      })
      .then((result) => {
        setTotalRecords(result.total);
        setAgendamentos(result.agendamentos || []);
        setLoading(false);
      })
      .catch((error) => {
        mostrarMensagem(error.response?.data || error, "error");
        setLoading(false);
      });
  }, [lazyState, filtros, mostrarMensagem]);

  useEffect(() => {
    pesquisarAgendamentos();
  }, [pesquisarAgendamentos]);

  useEffect(() => {
    apoioService.listaSituacaoAgendamento().then((result) => setSituacoes(result));
  }, []);

  /** Todo filtro vale assim que é escolhido — inclusive a navegação por período. */
  const alterarFiltro = (alteracoes) => {
    setFiltros({ ...filtros, ...alteracoes });
    setLazyState({ ...lazyState, first: 0, page: 0 });
  };

  const limparFiltros = () => {
    setFiltros(filtrosIniciais());
    setLazyState(LAZY_INICIAL);
    setPeriodo({ modo: PERIODO_PADRAO, referencia: new Date() });
  };

  const aplicarPeriodo = (modo, referencia) => {
    if (!modo) {
      setPeriodo({ modo: null, referencia: referencia });
      alterarFiltro({ dataInicial: null, dataFinal: null });
      return;
    }

    const { inicio, fim } = intervaloPeriodo(modo, referencia);
    setPeriodo({ modo: modo, referencia: referencia });
    alterarFiltro({ dataInicial: inicio, dataFinal: fim });
  };

  const navegarPeriodo = (passos) => {
    const modo = periodo.modo || PERIODO_PADRAO;
    aplicarPeriodo(modo, deslocarReferencia(modo, periodo.referencia, passos));
  };

  const irParaHoje = () => aplicarPeriodo(periodo.modo || PERIODO_PADRAO, new Date());

  /** Editar as datas na mão desliga a navegação por período. */
  const alterarData = (campo, valor) => {
    setPeriodo({ modo: null, referencia: valor || periodo.referencia });
    alterarFiltro({ [campo]: valor });
  };

  const onPage = (event) => {
    setLazyState({ ...lazyState, first: event.first, rows: event.rows, page: event.page });
  };

  const onSort = (event) => {
    setLazyState({ ...lazyState, sortField: event.sortField, sortOrder: event.sortOrder === 1, first: 0, page: 0 });
  };

  const abrirDetalhe = (rowData) => {
    setAgendamento(rowData);
    setDialogInfoVisible(true);
  };

  const descricaoSituacao = (id) => situacoes.find((situacao) => situacao.id === id)?.descricao || "";

  /** Chips do que está filtrado: continuam visíveis com o painel de filtros recolhido. */
  const filtrosAtivos = [
    filtros.paciente && { chave: "paciente", rotulo: `Paciente: ${filtros.paciente.nome}`, limpar: { paciente: null } },
    filtros.profissional && {
      chave: "profissional",
      rotulo: `Profissional: ${filtros.profissional.nome}`,
      limpar: { profissional: null },
    },
    filtros.sala && { chave: "sala", rotulo: `Sala: ${filtros.sala.nome}`, limpar: { sala: null } },
    filtros.situacaoAgendamentoId && {
      chave: "situacao",
      rotulo: `Situação: ${descricaoSituacao(filtros.situacaoAgendamentoId)}`,
      limpar: { situacaoAgendamentoId: null },
    },
    filtros.presencial !== null && {
      chave: "modalidade",
      rotulo: filtros.presencial ? "Presencial" : "Online",
      limpar: { presencial: null },
    },
    !periodo.modo &&
      (filtros.dataInicial || filtros.dataFinal) && {
        chave: "periodo",
        rotulo: `Período: ${converterData(filtros.dataInicial) || "início"} a ${
          converterData(filtros.dataFinal) || "fim"
        }`,
        limpar: { dataInicial: null, dataFinal: null },
      },
  ].filter(Boolean);

  const tagSituacao = (rowData) => {
    const situacao = situacaoAgendamento(rowData.situacaoAgendamentoId);
    return (
      <Tag
        value={rowData.situacaoAgendamentoDescricao}
        style={{ backgroundColor: situacao.cor, color: situacao.corTexto }}
      />
    );
  };

  const campoSelecao = (label, valor, onClick, onLimpar) => (
    <>
      <label htmlFor={label}>{label}</label>
      <div className="flex">
        <InputText
          id={label}
          value={valor || ""}
          placeholder="Todos"
          className="w-full flex-grow-1 mr-2"
          style={{ cursor: "pointer" }}
          onClick={onClick}
          readOnly
        />
        {valor ? (
          <Button
            icon="pi pi-times"
            className="flex-none"
            severity="secondary"
            outlined
            aria-label={`Remover filtro de ${label.toLowerCase()}`}
            onClick={onLimpar}
          />
        ) : (
          <Button
            icon="pi pi-search"
            className="flex-none"
            aria-label={`Escolher ${label.toLowerCase()}`}
            onClick={onClick}
          />
        )}
      </div>
    </>
  );

  const cartaoAgendamento = (rowData) => {
    const situacao = situacaoAgendamento(rowData.situacaoAgendamentoId);
    return (
      <div
        className="consulta-cartao"
        style={{ borderLeftColor: situacao.cor }}
        role="button"
        tabIndex={0}
        onClick={() => abrirDetalhe(rowData)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && abrirDetalhe(rowData)}
      >
        <div className="consulta-cartao-topo">
          <span className="consulta-cartao-horario">
            {rowData.horaInicial} - {rowData.horaFinal}
          </span>
          {tagSituacao(rowData)}
        </div>
        <p className="consulta-cartao-paciente">{rowData.pacienteNome}</p>
        <p className="consulta-cartao-linha">
          <i className="pi pi-user" aria-hidden="true" /> {rowData.profissionalNome}
        </p>
        <div className="consulta-cartao-rodape">
          <span>
            <i className="pi pi-calendar" aria-hidden="true" /> {formatarData(rowData.dataAgendamento)}
          </span>
          <span>
            <i className={`pi ${rowData.salaNome ? "pi-map-marker" : "pi-video"}`} aria-hidden="true" />{" "}
            {rowData.salaNome || "Online"}
          </span>
          <span className="consulta-cartao-valor">{converterValor(rowData.valor)}</span>
        </div>
      </div>
    );
  };

  const semResultados = (
    <div className="consulta-vazio">
      <i className="pi pi-calendar-times" aria-hidden="true" />
      <p className="consulta-vazio-titulo">Nenhum agendamento neste período</p>
      <p className="consulta-vazio-texto">Navegue para outro período ou ajuste os filtros.</p>
      {(filtrosAtivos.length > 0 || periodo.modo) && (
        <Button label="Limpar filtros" icon="pi pi-filter-slash" text onClick={limparFiltros} />
      )}
    </div>
  );

  return (
    <>
      <Card title="Consulta de Agendamentos">
        <div className="consulta-periodo">
          <div className="consulta-periodo-controles">
            <Button
              icon="pi pi-chevron-left"
              severity="secondary"
              outlined
              aria-label="Período anterior"
              tooltip="Período anterior"
              onClick={() => navegarPeriodo(-1)}
            />
            <Button label="Hoje" severity="secondary" outlined onClick={irParaHoje} />
            <Button
              icon="pi pi-chevron-right"
              severity="secondary"
              outlined
              aria-label="Próximo período"
              tooltip="Próximo período"
              onClick={() => navegarPeriodo(1)}
            />
            <SelectButton
              value={periodo.modo}
              options={PERIODOS}
              onChange={(e) => aplicarPeriodo(e.value, periodo.referencia)}
              allowEmpty
              aria-label="Tamanho do período"
            />
          </div>
          <div className="consulta-periodo-rotulo">
            <span className="consulta-periodo-titulo">
              {rotularPeriodo(periodo.modo, periodo.referencia, filtros.dataInicial || filtros.dataFinal)}
            </span>
            <span className="consulta-periodo-total" aria-live="polite">
              {loading ? "carregando..." : `${totalRecords} agendamento${totalRecords === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>

        {filtrosAtivos.length > 0 && (
          <div className="consulta-chips">
            {filtrosAtivos.map((filtro) => (
              <Chip key={filtro.chave} label={filtro.rotulo} removable onRemove={() => alterarFiltro(filtro.limpar)} />
            ))}
            <Button label="Limpar filtros" icon="pi pi-filter-slash" text size="small" onClick={limparFiltros} />
          </div>
        )}

        <Panel
          className="consulta-filtros"
          toggleable
          collapsed={filtrosRecolhidos}
          header={
            <span>
              Filtros
              {filtrosAtivos.length > 0 && <Badge value={filtrosAtivos.length} className="ml-2" />}
            </span>
          }
        >
          <div className="formgrid grid">
            <div className="field col-12 md:col-4" hidden={!permissaoPesquisaPaciente}>
              {campoSelecao(
                "Paciente",
                filtros.paciente?.nome,
                () => setDialogPacienteVisible(true),
                () => alterarFiltro({ paciente: null })
              )}
            </div>
            <div className="field col-12 md:col-4" hidden={!permissaoPesquisaProfissional}>
              {campoSelecao(
                "Profissional",
                filtros.profissional?.nome,
                () => setDialogProfissionalVisible(true),
                () => alterarFiltro({ profissional: null })
              )}
            </div>
            <div className="field col-12 md:col-4" hidden={!permissaoPesquisaSala}>
              {campoSelecao(
                "Sala",
                filtros.sala?.nome,
                () => setDialogSalaVisible(true),
                () => alterarFiltro({ sala: null })
              )}
            </div>

            <div className="field col-12 md:col-3">
              <label htmlFor="dataInicial">Data inicial</label>
              <Calendar
                id="dataInicial"
                value={filtros.dataInicial}
                onChange={(e) => alterarData("dataInicial", e.value)}
                dateFormat="dd/mm/yy"
                className="w-full"
                placeholder="dd/mm/aaaa"
                showIcon
                showButtonBar
                touchUI={isMobile}
                showOnFocus={false}
                mask="99/99/9999"
              />
            </div>
            <div className="field col-12 md:col-3">
              <label htmlFor="dataFinal">Data final</label>
              <Calendar
                id="dataFinal"
                value={filtros.dataFinal}
                onChange={(e) => alterarData("dataFinal", e.value)}
                dateFormat="dd/mm/yy"
                className="w-full"
                placeholder="dd/mm/aaaa"
                showIcon
                showButtonBar
                touchUI={isMobile}
                showOnFocus={false}
                mask="99/99/9999"
              />
            </div>
            <div className="field col-6 md:col-3">
              <label htmlFor="situacao">Situação</label>
              <Dropdown
                id="situacao"
                value={filtros.situacaoAgendamentoId}
                options={situacoes}
                optionLabel="descricao"
                optionValue="id"
                onChange={(e) => alterarFiltro({ situacaoAgendamentoId: e.value })}
                placeholder="Todas"
                className="w-full"
                showClear
              />
            </div>
            <div className="field col-6 md:col-3">
              <label htmlFor="modalidade">Modalidade</label>
              <Dropdown
                id="modalidade"
                value={filtros.presencial}
                options={MODALIDADES}
                onChange={(e) => alterarFiltro({ presencial: e.value })}
                placeholder="Todas"
                className="w-full"
                showClear
              />
            </div>
          </div>
        </Panel>

        {isMobile && (
          <div className="consulta-ordenacao">
            <Dropdown
              id="ordenacao"
              value={`${lazyState.sortField}:${lazyState.sortOrder}`}
              options={ORDENACOES}
              onChange={(e) => {
                const [campo, ordem] = e.value.split(":");
                setLazyState({ ...lazyState, sortField: campo, sortOrder: ordem === "true", first: 0, page: 0 });
              }}
              dropdownIcon="pi pi-sort-alt"
              aria-label="Ordenar por"
            />
          </div>
        )}

        <DataTable
          value={agendamentos}
          paginator
          lazy
          rows={lazyState.rows}
          rowsPerPageOptions={isMobile ? undefined : [5, 10, 25, 50]}
          first={lazyState.first}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          sortField={lazyState.sortField}
          sortOrder={lazyState.sortOrder ? 1 : -1}
          loading={loading}
          dataKey="id"
          emptyMessage={semResultados}
          className={isMobile ? "consulta-lista" : "p-datatable-sm consulta-tabela"}
          stripedRows={!isMobile}
          onRowClick={(e) => !isMobile && abrirDetalhe(e.data)}
          paginatorTemplate={
            isMobile
              ? "PrevPageLink CurrentPageReport NextPageLink"
              : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
          }
          currentPageReportTemplate={
            isMobile ? "{first}-{last} de {totalRecords}" : "{first} a {last} de {totalRecords} agendamentos"
          }
        >
          {isMobile ? (
            <Column body={cartaoAgendamento} />
          ) : (
            <Column
              field="dataAgendamento"
              header="Data"
              sortable
              style={{ width: "7rem", whiteSpace: "nowrap" }}
              body={(rowData) => formatarData(rowData.dataAgendamento)}
            />
          )}
          {!isMobile && (
            <Column
              field="horaInicial"
              header="Horário"
              sortable
              style={{ width: "8rem", whiteSpace: "nowrap" }}
              body={(rowData) => `${rowData.horaInicial} - ${rowData.horaFinal}`}
            />
          )}
          {!isMobile && <Column field="pacienteNome" header="Paciente" sortable />}
          {!isMobile && <Column field="profissionalNome" header="Profissional" sortable />}
          {!isMobile && (
            <Column
              field="salaNome"
              header="Local"
              sortable
              body={(rowData) => rowData.salaNome || <span className="consulta-online">Online</span>}
            />
          )}
          {!isMobile && (
            <Column
              field="situacaoAgendamentoId"
              header="Situação"
              sortable
              body={tagSituacao}
              style={{ width: "11rem" }}
            />
          )}
          {!isMobile && (
            <Column
              field="valor"
              header="Valor"
              sortable
              style={{ width: "7rem", textAlign: "right", whiteSpace: "nowrap" }}
              body={(rowData) => converterValor(rowData.valor)}
            />
          )}
          {!isMobile && (
            <Column
              style={{ width: "3rem" }}
              body={(rowData) => (
                <Button
                  icon="pi pi-eye"
                  size="small"
                  text
                  aria-label={`Ver detalhes do agendamento de ${rowData.pacienteNome}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    abrirDetalhe(rowData);
                  }}
                />
              )}
            />
          )}
        </DataTable>
      </Card>

      {dialogPacienteVisible && (
        <DialogPaciente
          dialogPacienteVisible={dialogPacienteVisible}
          setDialogPacienteVisible={setDialogPacienteVisible}
          onSelectMetodo={(paciente) => alterarFiltro({ paciente: paciente })}
        />
      )}

      {dialogProfissionalVisible && (
        <DialogProfissional
          dialogProfissionalVisible={dialogProfissionalVisible}
          setDialogProfissionalVisible={setDialogProfissionalVisible}
          onSelectMetodo={(profissional) => alterarFiltro({ profissional: profissional })}
        />
      )}

      {dialogSalaVisible && (
        <DialogSala
          dialogSalaVisible={dialogSalaVisible}
          setDialogSalaVisibleVisible={setDialogSalaVisible}
          onSelectMetodo={(sala) => alterarFiltro({ sala: sala })}
        />
      )}

      <DialogAgendamentoInfo
        dialogAgendamentoVisible={dialogInfoVisible}
        setDialogAgendamentoVisible={setDialogInfoVisible}
        atualizar={() => pesquisarAgendamentos()}
        agendamento={agendamento}
        setAgendamento={setAgendamento}
        mostrarMensagem={mostrarMensagem}
      />
    </>
  );
};

export default AgendamentoConsulta;
